const BorderModifiers = [
  "border-color:",
  "border-thickness:",
  "corner-radius:",
  "padding-bottom:",
  "padding-left:",
  "padding-right:",
  "padding-top:",
  "padding:",
] as const;

const GlobalModifiers = ["delay:", "loop:", "effect:", "caption:"] as const;

export const alignOptions = ["left", "center", "right", "justify"] as const;
export const anchorOptions = ["center", "top", "bottom"] as const;
export const autospaceOptions = ["on", "off"] as const


export const decorationOptions = [
  "underline",
  "line-through",
  "overline",
  "underline overline",
  "underline line-through",
] as const;

export const chartTypeOptions = ["bar", "line"] as const;

export const moveByOptions = ["scroll", "tap", "scrollCards", "scrollContinuous"] as const
export const defaultNavigationIndex = 1

export const animateOptions = [
  "dropAndExpand::m23",
] as const;

export const weightOptions = [
  "light",
  "bold",
] as const;

export const hideOnOptions = ["desktop", "mobile"] as const

export const shadowOptions = [
  "normal",
  "over-image",
] as const;

const ChartModifiers = [
  "chart-type:"
] as const;

const ImageModifiers = [
  "blur:",
  "hideOn:"
] as const;

export const LinkModifiers = ["backlink:", "url:", "toPage:", "mailto:"] as const;

const PositionModifiers = ["across:", "animate:", "down:", "align:", "anchor:", "animateStart:"] as const;

export const TextDecorations = {
  underline: "__",
  bold: "**",
  italic: "''",
  shadow: "§",
  strikeThrough: "~~",
} as const;

export const TextEffects = {
  animate: ">>>",
  endAnimate: "<<<",
} as const;

export const TextLinks = {
  link: "{{",
  endLink: "}}",
} as const;

const TextModifiers = ["columns:", "decoration:", "line-height:", "shadow:", "weight:"] as const;

export const VideoModifiers = [
  "mute:", "widescreen:"
] as const;

const SizeModifiers = ["height:", "width:"] as const;

const TopModifiers = [
  "background:",
  "color:",
  "font:",
  "opacity:",
  "size:",
] as const;


// allow the link modifiers to hold an array of links to allow the user to use more links in a sentence than just one of each type
export type ModifierArrayKeys = "backlink:" | "toPage:" | "url:" | "mailto:"


// TODO: This does not catch the absence of modifiers
export const allModifiers = [
  ...BorderModifiers,
  ...ChartModifiers,
  ...GlobalModifiers,
  ...ImageModifiers,
  ...LinkModifiers,
  ...PositionModifiers,
  ...SizeModifiers,
  ...TextModifiers,
  ...TopModifiers,
  ...VideoModifiers,

];


export const styleModifiers = [
  ...BorderModifiers,
  ...ImageModifiers,
  ...PositionModifiers,
  ...SizeModifiers,
  ...TextModifiers,
  ...TopModifiers,
];


export type Mods = { [K in AllModifiersType]?: string };
export type Modifier = Mods & { links?: { type: ModifierArrayKeys, link: string }[] }

export type AllModifiersType = typeof allModifiers[number];
export type StyleModifiersType = typeof styleModifiers[number];

export type ModifiersSubSet = typeof allModifiers[number][];
export type ModifierAutoComplete = Array<
  "animate"
  | "audio"
  | "charts"
  | "chartType"
  | "colors"
  | "decoration"
  | "effect"
  | "filters"
  | "fonts"
  | "hide"
  | "images"
  | "loop"
  | "moveBy"
  | "placement"
  | "stackCfKeys"
  | "toggle"
  | "weight"
>;

export type ModifierObjectType = {
  [K in AllModifiersType]: {
    keyword: typeof allModifiers[number];
    autoComplete?: ModifierAutoComplete
    help: string;
    expandedHelp?: string; // NEW FIELD
    hide?: boolean;        // NEW FIELD
  };
};

export const modifierObject: ModifierObjectType = {
  // "~:": {
  //   keyword: "~:",
  //   help: "Choose a text effect: wiggle, neon, rainbow",
  //   expandedHelp: "",
  //   hide: false,
  // },
  "align:": {
    keyword: "align:",
    help: "Align your content.",
    autoComplete: ["placement"],
    expandedHelp: "You can align your text and other items to the `left`, `right`, or `center`. For paragraphs, you can also use `justify`.",
    hide: false,
  },
  "across:": {
    keyword: "across:",
    help: "Place something horizontally.",
    expandedHelp: "This lets you place an item from the left side of the page. Just use a percentage. For example, `across: 50` will place it halfway across the page.",
    hide: false,
  },
  "anchor:": {
    keyword: "anchor:",
    help: "Anchor your item.",
    autoComplete: ["animate"],
    expandedHelp: "You can anchor an item to the `top`, `center`, or `bottom` of its space. This is useful for keeping things in place.",
    hide: true,
  },
  "animate:": {
    keyword: "animate:",
    help: "Animate an item.",
    autoComplete: ["placement"],
    expandedHelp: "Add a bit of motion to your items. You can choose from a list of animations to bring your page to life.",
    hide: true,
  },
  "animateStart:": {
    keyword: "animateStart:",
    help: "Time your animations.",
    autoComplete: ["placement"],
    expandedHelp: "This lets you control exactly when an animation begins in relation to the one before it.",
    hide: true,
  },
  "background:": {
    keyword: "background:",
    help: "Add a background color or image.",
    autoComplete: ["colors", "images"],
    expandedHelp: "You can set a background for an item with a color or an image you've uploaded.",
    hide: false,
  },
  "backlink:": {
    keyword: "backlink:",
    help: "Link to another story.",
    autoComplete: ["stackCfKeys"],
    expandedHelp: "Use this to create a link to another one of your QuickPoint stories.",
    hide: false,
  },
  "border-color:": {
    keyword: "border-color:",
    help: "Choose a border color.",
    autoComplete: ["colors"],
    expandedHelp: "Pick a color for the border of an item. Just type a color name after the modifier.",
    hide: false,
  },
  "border-thickness:": {
    keyword: "border-thickness:",
    help: "Change the border thickness.",
    expandedHelp: "You can make the border of an item thicker or thinner. Just add a number.",
    hide: false,
  },
  "blur:": {
    keyword: "blur:",
    help: "Blur an item.",
    expandedHelp: "Add a blur effect to an item. The higher the number, the more blurry it will be. For example: `blur: 1.3`",
    hide: false,
  },
  "chart-type:": {
    keyword: "chart-type:",
    help: "Choose a chart type.",
    autoComplete: ["chartType"],
    expandedHelp: "By default QuickPoint picks the chart type from your data. Use `chart-type: bar` or `chart-type: line` to choose one yourself.",
    hide: false,
  },
  "caption:": {
    keyword: "caption:",
    help: "Add a caption.",
    expandedHelp: "Use this to add a caption to an image or video.",
    hide: false,
  },
  "color:": {
    keyword: "color:",
    help: "Choose a color from the palette.",
    autoComplete: ["colors"],
    expandedHelp: "Your chosen filter provides a color palette. Use `first` for the main color, `second` for the next, and so on, to keep your design consistent.",
    hide: false,
  },
  "columns:": {
    keyword: "columns:",
    help: "Split text into columns.",
    expandedHelp: "You can split a paragraph into multiple columns. Just add a number, like `columns: 2`.",
    hide: false,
  },
  "corner-radius:": {
    keyword: "corner-radius:",
    help: "Round the corners.",
    expandedHelp: "Use this to make the corners of an item more rounded. The higher the number, the rounder they'll get.",
    hide: false,
  },
  "decoration:": {
    keyword: "decoration:",
    help: "Decorate your text.",
    autoComplete: ["decoration"],
    expandedHelp: "You can add an `underline`, `overline`, or `line-through` to your text.",
    hide: false,
  },
  "delay:": {
    keyword: "delay:",
    help: "Set a delay for autoplay.",
    expandedHelp: "When using `autoplay:`, you can use `delay:` to add an extra pause before the next page loads. Just add a number in seconds, like `delay: 2`.",
    hide: false,
  },
  "down:": {
    keyword: "down:",
    help: "Place something vertically.",
    expandedHelp: "This lets you place an item from the top of the page. Just use a percentage. For example, `down: 50` will place it halfway down the page.",
    hide: false,
  },
  "effect:": {
    keyword: "effect:",
    help: "Add a special text effect.",
    autoComplete: ["effect"],
    expandedHelp: "You can add special effects to your text, like `neon` or `rainbow`.",
    hide: true,
  },
  "font:": {
    keyword: "font:",
    help: "Choose a font.",
    autoComplete: ["fonts"],
    expandedHelp: "Pick a font for your text. Just type the name of the font after the modifier.",
    hide: false,
  },
  "hideOn:": {
    keyword: "hideOn:",
    help: "Hide an item on desktop or mobile.",
    autoComplete: ["hide"],
    expandedHelp: "You can hide an item so it only shows up on certain devices. Use `hideOn: desktop` to hide it on computers, or `hideOn: mobile` to hide it on phones.",
    hide: false,
  },
  "loop:": {
    keyword: "loop:",
    help: "Loop your auto playing story.",
    autoComplete: ["loop"],
    expandedHelp: "When you're using `autoplay:`, you can make your story loop back to the beginning when it's finished. Just use `loop: on`.",
    hide: true,
  },
  "toPage:": {
    keyword: "toPage:",
    help: "Link to another page.",
    expandedHelp: "Create a link to another page in your story. Just use the page number, like `toPage: 3`.",
    hide: false,
  },
  "height:": {
    keyword: "height:",
    help: "Change the height.",
    expandedHelp: "You can change the height of an item. Just add a number.",
    hide: false,
  },
  "line-height:": {
    keyword: "line-height:",
    help: "Change the line height.",
    expandedHelp: "This lets you control the space between lines of text. A smaller number will bring them closer together, and a larger number will spread them out.",
    hide: false,
  },
  "mailto:": {
    keyword: "mailto:",
    help: "Create an email link.",
    expandedHelp: "This makes a link that will open up a new email. Just add the email address you want it to go to, like `mailto: hello@example.com`.",
    hide: false,
  },
  "mute:": {
    keyword: "mute:",
    help: "Mute a video.",
    autoComplete: ["toggle"],
    expandedHelp: "Your video will start with the sound off. Your viewers can unmute it if they want.",
    hide: false,
  },
  "opacity:": {
    keyword: "opacity:",
    help: "Change the transparency.",
    expandedHelp: "You can make an item more or less transparent. Use a number from 0 (completely invisible) to 1 (completely solid).",
    hide: false,
  },
  "padding:": {
    keyword: "padding:",
    help: "Add space around an item.",
    expandedHelp: "This adds a bit of space around an item, inside its border. Just add a number.",
    hide: false,
  },
  "padding-bottom:": {
    keyword: "padding-bottom:",
    help: "Add space below an item.",
    expandedHelp: "",
    hide: false,
  },
  "padding-left:": {
    keyword: "padding-left:",
    help: "Add space to the left of an item.",
    expandedHelp: "",
    hide: false,
  },
  "padding-right:": {
    keyword: "padding-right:",
    help: "Add space to the right of an item.",
    expandedHelp: "",
    hide: false,
  },
  "padding-top:": {
    keyword: "padding-top:",
    help: "Add space above an item.",
    expandedHelp: "",
    hide: false,
  },
  "size:": {
    keyword: "size:",
    help: "Choose a size.",
    expandedHelp: "You can change the size of text and other items. Just add a number.",
    hide: false,
  },
  "shadow:": {
    keyword: "shadow:",
    help: "Add a shadow.",
    autoComplete: ["colors"],
    expandedHelp: "You can add a shadow to text and other items.",
    hide: false,
  },
  "url:": {
    keyword: "url:",
    help: "Link to a website.",
    expandedHelp: "Create a link to an external website. Just paste the URL after the modifier.",
    hide: false,
  },
  "weight:": {
    keyword: "weight:",
    help: "Change the font weight.",
    autoComplete: ["weight"],
    expandedHelp: "You can make your text `bold`, `bolder`, `lighter`, or `normal`.",
    hide: false,
  },
  "widescreen:": {
    keyword: "widescreen:",
    help: "Make your video widescreen.",
    autoComplete: ["toggle"],
    expandedHelp: "This will make your video fill the full width of the page.",
    hide: false,
  },
  "width:": {
    keyword: "width:",
    help: "Change the width.",
    expandedHelp: "You can change the width of an item. Just add a number.",
    hide: false,
  },
};

export function modifiersWithAutoComplete() {
  const res = [];
  for (const key in modifierObject) {
    const k = key as typeof allModifiers[number];
    if (modifierObject[k].autoComplete && !modifierObject[k].hide) {
      res.push(k);
    }
  }
  return res;
}

export function getModifierCategory(mod: typeof allModifiers[number]) {
  switch (true) {
    // @ts-ignore
    case BorderModifiers.includes(mod):
      return "Borders";
    // @ts-ignore
    case ChartModifiers.includes(mod):
      return "Charts";
    // @ts-ignore
    case GlobalModifiers.includes(mod):
      return "Xtra";
    // @ts-ignore
    case ImageModifiers.includes(mod):
      return "Images"; // @ts-ignore
    case LinkModifiers.includes(mod):
      return "Links";
    // @ts-ignore
    case PositionModifiers.includes(mod):
      return "Position";
    // @ts-ignore;
    case SizeModifiers.includes(mod):
      return "Size";
    // @ts-ignore
    case TextModifiers.includes(mod):
      return "Text";
    // @ts-ignore
    case TopModifiers.includes(mod):
      return "";
    // @ts-ignore
    case VideoModifiers.includes(mod):
      return "Video";

    default:
      return "Unknown";
  }
}
